<script>
    import { signer } from "svelte-ethers-store";
    import { formatUnits } from "ethers/lib/utils";
    import { BigNumber, ethers } from "ethers";
    import ReserveTokenArtifact from "../../../../../abi/ReserveToken.json";
    import Button from "../../../../../components/button/Button.svelte";
    import Ring from "../../../../../components/ring/Ring.svelte";
    import {
        GET_TRANSACTION_LINK,
        TRANSACTION_FEE_PERCENTAGE,
    } from "../../../../../constants/constants";
    import Steps from "../../../../../components/steps/Steps.svelte";
    import { formatNumber } from "../../../../../utils";
    import {isTransactionConfirmed, projectDetail} from "../../../../../store";

    const TxStatus = {
        None: "None",
        AwaitingSignature: "AwaitingSignature",
        AwaitingConfirmation: "AwaitingConfirmation",
        Error: "Error",
    };

    const RefundSteps = {
        Approve: "Approve",
        Confirm: "Confirm",
        Complete: "Complete",
    };

    export let transaction,
        token,
        reserve,
        saleContract,
        sale,
        errorMsg,
        txReceipt,
        activeStep = RefundSteps.Approve,
        txStatus = TxStatus.None;

    $: reserve = reserve;
    $: sale = sale;
    $: saleContract = saleContract;

    const receipt = transaction.receipt;

    const approve = async () => {
        const rTKN = new ethers.Contract(
            token.id,
            ReserveTokenArtifact.abi,
            $signer
        );

        let tx;
        txStatus = TxStatus.AwaitingSignature;

        try {
            tx = await rTKN.approve(
                saleContract.address,
                BigNumber.from(receipt.units)
            );
        } catch (error) {
            errorMsg = error.data?.message || error?.message;
            txStatus = TxStatus.Error;
            if (errorMsg.length > 500) errorMsg = "Something went wrong";
            return;
        }

        txStatus = TxStatus.AwaitingConfirmation;
        const txReceipt = await tx.wait();

        txStatus = TxStatus.None;
        activeStep = RefundSteps.Confirm;
        return txReceipt;
    };

    const refund = async () => {
        let tx;
        txStatus = TxStatus.AwaitingSignature;
        try {
            tx = await saleContract.refund({
                id: BigNumber.from(receipt.receiptId),
                feeRecipient: receipt.feeRecipient,
                fee: BigNumber.from(receipt.fee),
                units: BigNumber.from(receipt.units),
                price: BigNumber.from(receipt.price),
            });
        } catch (error) {
            errorMsg = error.data?.message || error?.message;
            txStatus = TxStatus.Error;
            if (errorMsg.length > 500) errorMsg = "Something went wrong";
            return;
        }

        txStatus = TxStatus.AwaitingConfirmation;
        txReceipt = await tx.wait();

        txStatus = TxStatus.None;
        activeStep = RefundSteps.Complete;
        setTimeout(() => {
            isTransactionConfirmed.set(true);
        }, 5000);
        return txReceipt;
    };
</script>

{#if txStatus === TxStatus.None}
    <div
        class="flex w-600 flex-col items-start gap-y-7 column-direction refunding-modal"
    >
        <Steps
            steps={["Approve", "Confirm", "Complete"]}
            {activeStep}
            fulfilledTextClass="text-gray-100"
            lineBorderClass="border-gray-400"
        />
        <h1 class="modal-title">Refund {token.symbol}</h1>
        <div
            class="grid grid-cols-2 gap-4 rounded-md border border-gray-600 p-4"
        >
            <span class="title"
                >Refund: {formatUnits(
                    transaction.receipt.units,
                    token.decimals
                )}
                {token.symbol}</span
            >
            <span class="title"
                >Price: {formatNumber(
                    transaction.receipt.price,
                    transaction.saleContract.reserve.decimals
                )}
                {reserve.symbol} per {token.symbol}</span
            >
            <span class="title">Fee: {TRANSACTION_FEE_PERCENTAGE}%</span>
            <span class="title"
                >Total: {formatUnits(transaction.totalIn, reserve.decimals)}
                {reserve.symbol}</span
            >
        </div>

        {#if activeStep === RefundSteps.Approve}
            <Button buttonClass={"submit-btn "} on:click={approve}
                >Approve</Button
            >
        {/if}

        {#if activeStep === RefundSteps.Confirm}
            <Button buttonClass={"submit-btn "} on:click={refund}
                >Confirm</Button
            >
        {/if}

        {#if activeStep === RefundSteps.Complete}
            <span class="title">Refund confirmed!</span>
            <a
                class="text-blue-400 underline"
                target="_blank"
                href={ GET_TRANSACTION_LINK($projectDetail.chain.chain_id, txReceipt?.transactionHash) }
            >
                See transaction.
            </a>
        {/if}
    </div>
{/if}

{#if txStatus === TxStatus.AwaitingSignature}
    <div class="flex flex-col items-center gap-y-5 p-6">
        <Ring color="#fff" />
        <span class="text-lg">Awaiting signature...</span>
    </div>
{/if}
{#if txStatus === TxStatus.AwaitingConfirmation}
    <div class="flex flex-col items-center gap-y-5 p-6">
        <Ring color="#fff" />
        <span class="text-lg">Transaction confirming...</span>
    </div>
{/if}
{#if txStatus === TxStatus.Error}
    <div class="flex flex-col items-center gap-y-5 p-6">
        <span class="text-lg">Something went wrong.</span>
        <span class="text-lg text-red-400">{errorMsg}</span>
    </div>
{/if}

<style>
    .refunding-modal span.title {
        font-size: 18px;
        line-height: 22px;
        color: #fff !important;
        margin: 0 0 15px;
        display: block !important;
    }
    span.text-lg {
        color: #fff;
    }
</style>
